import type { RiskScoreBreakdown } from '@risksmart-app/domain/src/types/risk-score-breakdown';
import { useCallback, useMemo } from 'react';

import { useGetRiskListOnlyOptimized } from '@/hooks/queries';

export type ResolveChildLabel = (riskId: string) => string;

/**
 * Resolves the child risk ids in an `aggregated` breakdown to titles at render
 * time — the payload stores none, as titles drift (RSP-7285). Reads the org's
 * light risk list, the same one the risk pickers hold, so a child the user
 * cannot see (or one not yet loaded) falls back to its id rather than a blank.
 */
export const useChildRiskLabels = (
  breakdown: RiskScoreBreakdown
): ResolveChildLabel => {
  const { data } = useGetRiskListOnlyOptimized({
    queryArgs: {},
    shouldSkip: breakdown.status !== 'aggregated',
  });
  const titlesById = useMemo(
    () =>
      new Map<string, string>(
        data?.risk.map((risk) => [risk.Id, risk.Title] as const)
      ),
    [data?.risk]
  );

  return useCallback(
    (riskId: string) => titlesById.get(riskId) ?? riskId,
    [titlesById]
  );
};
